import type { TWipeRevealRole } from "../shared/motion/wipe-reveal-targets";
import { QUESTIONNAIRE_JOURNEY_QUESTION, type TQuestionnairePath } from "./questionnaire-flow";

export const PICKER_INTRO_BEATS = ["robot", "greeting", "pitch", "choices"] as const;

export type TPickerIntroBeat = (typeof PICKER_INTRO_BEATS)[number];
export type TPickerIntroLine = "greeting" | "pitch" | "choices";
export type TPickerIntroMotion = "play" | "skip";

/** Origin S3 copy — played on load instead of scroll. */
export const PICKER_INTRO_GREETING = "Hey there! I'm Triffin AI, your Triffin agent.";
export const PICKER_INTRO_PITCH = "See how I can help you revolutionise your business finance operations...";
export const PICKER_INTRO_CHOICES = QUESTIONNAIRE_JOURNEY_QUESTION;

/** How long each beat holds before the next one starts. Wipe + a short readable pause. */
export const PICKER_INTRO_HOLD_MS = {
	robot: 550,
	greeting: 1300,
	pitch: 1250,
} as const satisfies Record<Exclude<TPickerIntroBeat, "choices">, number>;

/** Choice cards ease in on the last beat — opacity + y, slight stagger. */
export const PICKER_INTRO_CARD_ENTER = {
	y: 16,
	durationS: 0.5,
	staggerS: 0.1,
} as const;

/**
 * Stack height is robot + copy only. `50svh - 50%` centres that block in the
 * viewport; the lifted seat matches the old S3 agent stage so cards fit below.
 */
export const PICKER_INTRO_STACK_CENTER_CLASS = "translate-y-[calc(50svh-50%)]";
export const PICKER_INTRO_STACK_LIFTED_CLASS =
	"translate-y-[max(5.5rem,22svh)] max-lg:translate-y-[max(3.5rem,min(19svh,10rem))] lg:xshort:translate-y-[max(3rem,22svh)] max-lg:xshort:translate-y-[clamp(40px,calc(32.64svh_-_87.3px),121.6px)]";
/** Same seat as the lift, but padding so later turns can append below without overlapping. */
export const PICKER_INTRO_STACK_LIFTED_SPACE_CLASS =
	"pt-[max(5.5rem,22svh)] max-lg:pt-[max(3.5rem,min(19svh,10rem))] lg:xshort:pt-[max(3rem,22svh)] max-lg:xshort:pt-[clamp(40px,calc(32.64svh_-_87.3px),121.6px)]";
export const PICKER_INTRO_STACK_LIFT_CLASS =
	"transition-transform duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform";

/** fit=full canvas — width sets footprint; height follows the robot's square frame. */
export const PICKER_INTRO_ROBOT_CLASS = "aspect-square w-[clamp(112px,32vw,152px)] shrink-0";

/** First-click handoff: bubble, then selected tiles, then path step 1. Gaps are wide
 *  enough that each element reads as its own beat inside the shared ~0.9s fade. */
export const PICKER_HANDOFF_MS = {
	bubble: 450,
	tiles: 550,
} as const;

export const PICKER_HANDOFF_PHASES = ["idle", "bubble", "tiles", "ready"] as const;
export type TPickerHandoffPhase = (typeof PICKER_HANDOFF_PHASES)[number];

/**
 * Intro cards live in the transcript answer slot but overlay it — absolute so
 * stack centre height stays robot+copy only and the turn gap does not reserve flow.
 */
export const PICKER_INTRO_CARDS_SLOT_CLASS =
	"absolute left-1/2 top-full z-[1] mt-10 w-[min(1026px,calc(100%-32px))] -translate-x-1/2 max-lg:w-[min(300px,calc(100%-90px))]";

export function pickerIntroStackYClass(lifted: boolean, occupySpace = false): string {
	if (!lifted) return PICKER_INTRO_STACK_CENTER_CLASS;
	return occupySpace ? PICKER_INTRO_STACK_LIFTED_SPACE_CLASS : PICKER_INTRO_STACK_LIFTED_CLASS;
}

/** Compressed from the old scroll wipes so each line finishes inside its hold. */
export const PICKER_INTRO_WIPE_TIMING: Record<TPickerIntroLine, { duration: number; delay: number }> = {
	greeting: { duration: 0.9, delay: 0 },
	pitch: { duration: 0.85, delay: 0 },
	choices: { duration: 0.8, delay: 0 },
};

export const PICKER_INTRO_WIPE_OUT_S = 0.7;

/** Deep-linked `?path=` skips the picker intro and opens the flow. */
export function shouldPlayPickerIntro(path: TQuestionnairePath | null): boolean {
	return path === null;
}

export function initialPickerIntroBeat(motion: TPickerIntroMotion): TPickerIntroBeat {
	return motion === "skip" ? "choices" : "robot";
}

export function nextPickerIntroBeat(beat: TPickerIntroBeat): TPickerIntroBeat | null {
	const nextIndex = PICKER_INTRO_BEATS.indexOf(beat) + 1;
	return PICKER_INTRO_BEATS[nextIndex] ?? null;
}

export function pickerIntroDelayAfter(beat: Exclude<TPickerIntroBeat, "choices">): number {
	return PICKER_INTRO_HOLD_MS[beat];
}

export function pickerIntroBeatAt(elapsedMs: number): TPickerIntroBeat {
	let elapsed = 0;
	for (const beat of ["robot", "greeting", "pitch"] as const) {
		elapsed += PICKER_INTRO_HOLD_MS[beat];
		if (elapsedMs < elapsed) return beat;
	}
	return "choices";
}

export function pickerIntroShowsCards(beat: TPickerIntroBeat): boolean {
	return beat === "choices";
}

export function pickerHandoffPhaseAt(elapsedMs: number, reducedMotion = false): TPickerHandoffPhase {
	if (reducedMotion) return "ready";
	if (elapsedMs < PICKER_HANDOFF_MS.bubble) return "bubble";
	if (elapsedMs < PICKER_HANDOFF_MS.bubble + PICKER_HANDOFF_MS.tiles) return "tiles";
	return "ready";
}

export function pickerHandoffShowsTiles(phase: TPickerHandoffPhase): boolean {
	return phase === "tiles" || phase === "ready";
}

export function pickerHandoffShowsStep(phase: TPickerHandoffPhase): boolean {
	return phase === "ready";
}

export function resolvePickerIntroLineRole(
	line: TPickerIntroLine,
	beat: TPickerIntroBeat,
	motion: TPickerIntroMotion = "play",
): TWipeRevealRole {
	if (motion === "skip") {
		return line === "choices" && beat === "choices" ? "shown" : "hidden";
	}
	if (line === beat) return "wipe-in";
	if (line === "greeting" && beat === "pitch") return "wipe-out";
	if (line === "pitch" && beat === "choices") return "wipe-out";
	return "hidden";
}
